import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Calendar, Trophy, Users, Clock, CheckCircle, XCircle, UserCheck, Filter, Download, X, IndianRupee, Mail, ClipboardList } from 'lucide-react';
import api, { uploadAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { useToast } from '../../contexts/ToastContext';
import { formatDateTimeLocal, formatDateTime, formatTime } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { EventWithHackathon } from '../../types/events';
import CustomFormFieldsManager from '../../components/ui/CustomFormFieldsManager';
import EventBriefImporter from '../../components/admin/EventBriefImporter';
import PaymentsPanel from '../../components/admin/PaymentsPanel';
import EmailPanel from '../../components/admin/EmailPanel';
import ResponsesPanel from '../../components/admin/ResponsesPanel';
import FormTeamPanel from '../../components/admin/FormTeamPanel';
import AddParticipant from '../../components/admin/AddParticipant';
import HackathonLinkField from '../../components/admin/HackathonLinkField';
import EventTypePicker from '../../components/admin/EventTypePicker';
import type { ParsedBrief } from '../../utils/eventBriefParser';
import type { CustomQuestionField } from '../../components/ui/CustomQuestionsFieldset';
import { validateFieldDefinitions } from '../../utils/formFieldValidation';
import { useFormDraft } from '../../hooks/useFormDraft';
import DraftBanner from '../../components/admin/DraftBanner';
import UserProfileModal from '../../components/ui/UserProfileModal';
import GuestDetailsModal from '../../components/ui/GuestDetailsModal';

interface Speaker {
  id: string;
  salutation: string;
  name: string;
  bio: string;
  achievements: string;
  picture: File | null;
  topic: string;
}

interface EventRegistration {
  _id: string;
  user?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  guestUser?: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    organization?: string;
  };
  event?: {
    _id: string;
    title: string;
    startTime: string;
    endTime: string;
  };
  hackathon?: {
    _id: string;
    title: string;
    startDate: string;
    endDate: string;
  };
  registrationType: 'event' | 'hackathon';
  registrationDate: string;
  status: 'registered' | 'confirmed' | 'attended' | 'withdrawn' | 'disqualified';
  notes?: string;
  confirmedBy?: string;
  confirmedAt?: string;
  attendedAt?: string;
  userType?: 'registered' | 'guest';
}

const EventManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'events' | 'registrations' | 'payments' | 'emails' | 'responses' | 'teams'>('events');
  
  // Events state
  const [events, setEvents] = useState<EventWithHackathon[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<EventWithHackathon | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Registrations state
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  // Straight from the registration schema. Every one of them is reachable now:
  // the buttons these replaced only ever moved forwards, and all of them
  // vanished at 'attended', so a mistake there could not be undone.
  const REGISTRATION_STATUSES = ['registered', 'confirmed', 'attended', 'withdrawn', 'disqualified'];

  const [registrationFilters, setRegistrationFilters] = useState({
    eventId: '',
    status: 'all',
    searchTerm: ''
  });
  const [registrationCounts, setRegistrationCounts] = useState({
    total: 0,
    byStatus: [] as Array<{_id: string, count: number}>,
    byEvent: [] as Array<{_id: string, count: number, eventTitle: string}>
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRegistrations, setSelectedRegistrations] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<'approve' | 'reject' | ''>('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [showDeleteRegistrationDialog, setShowDeleteRegistrationDialog] = useState(false);
  const [showRejectRegistrationDialog, setShowRejectRegistrationDialog] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState<{id: string, name: string} | null>(null);
  const [registrationToReject, setRegistrationToReject] = useState<{id: string, name: string} | null>(null);
  
  // Profile modal states
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [showGuestDetailsModal, setShowGuestDetailsModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedGuestData, setSelectedGuestData] = useState<any>(null);
  
  const { success, error } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (activeTab === 'events') {
      fetchEvents();
    } else if (activeTab === 'registrations') {
      fetchRegistrations();
      fetchRegistrationCounts();
    }
  }, [activeTab, registrationFilters, statusFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/admin/events?${params.toString()}`);
      setEvents(response.data);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    try {
      setRegistrationsLoading(true);
      const params = new URLSearchParams();
      if (registrationFilters.eventId) params.append('eventId', registrationFilters.eventId);
      if (registrationFilters.status !== 'all') params.append('status', registrationFilters.status);
      if (registrationFilters.searchTerm.trim()) params.append('search', registrationFilters.searchTerm.trim());
      
      const response = await api.get(`/registrations/admin/all?${params.toString()}`);
      setRegistrations(response.data);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch registrations');
    } finally {
      setRegistrationsLoading(false);
    }
  };

  const fetchRegistrationCounts = async () => {
    try {
      const params = new URLSearchParams();
      if (registrationFilters.eventId) params.append('eventId', registrationFilters.eventId);
      if (registrationFilters.status !== 'all') params.append('status', registrationFilters.status);
      if (registrationFilters.searchTerm.trim()) params.append('search', registrationFilters.searchTerm.trim());
      
      const response = await api.get(`/registrations/admin/counts?${params.toString()}`);
      setRegistrationCounts(response.data);
    } catch (err: any) {
      console.error('Failed to fetch registration counts:', err);
    }
  };

  const exportRegistrations = async () => {
    try {
      setExportLoading(true);
      console.log('🚀 Starting export...');
      
      const params = new URLSearchParams();
      if (registrationFilters.eventId) params.append('eventId', registrationFilters.eventId);
      if (registrationFilters.status !== 'all') params.append('status', registrationFilters.status);
      if (registrationFilters.searchTerm.trim()) params.append('search', registrationFilters.searchTerm.trim());
      params.append('format', 'excel');
      
      const url = `/api/registrations/admin/export?${params.toString()}`;
      console.log('📡 Making request to:', url);
      
      // For Excel, we need to handle the file download directly
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Include cookies for session-based auth
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Response error:', errorData);
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      console.log('📄 Getting blob...');
      const blob = await response.blob();
      console.log('📄 Blob size:', blob.size, 'bytes');
      console.log('📄 Blob type:', blob.type);
      
      const url2 = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url2;
      link.download = `registrations_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url2);
      
      console.log('✅ Export completed successfully');
      success(`Exported ${registrations.length} registrations to Excel`);
    } catch (err: any) {
      console.error('❌ Export error:', err);
      error(err.message || 'Failed to export registrations');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteEvent = (eventId: string) => {
    setEventToDelete(eventId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteEvent = async () => {
    if (!eventToDelete) return;

    try {
      await api.delete(`/admin/events/${eventToDelete}`);
      success('Event deleted successfully');
      fetchEvents();
      setShowDeleteDialog(false);
      setEventToDelete(null);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to delete event');
    }
  };

  const handleToggleEventStatus = async (eventId: string) => {
    try {
      await api.patch(`/admin/events/${eventId}/toggle-status`);
      success('Event status updated successfully');
      fetchEvents();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to update event status');
    }
  };

  const handleChangeStatus = async (registration: any, status: string) => {
    if (status === registration.status) return;
    const who = registration.user
      ? `${registration.user.firstName} ${registration.user.lastName}`
      : 'this registration';
    if (!window.confirm(`Change the status of ${who} from "${registration.status}" to "${status}"?`)) {
      // Nothing was sent, but the select has already moved. Reload so what is
      // on screen is what is stored.
      fetchRegistrations();
      return;
    }
    try {
      await api.patch(`/registrations/${registration._id}/status`, { status });
      success(`Status changed to ${status}`);
      fetchRegistrations();
      fetchRegistrationCounts();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not change that status');
      fetchRegistrations();
    }
  };

  const handleConfirmRegistration = async (registrationId: string) => {
    try {
      await api.post(`/registrations/${registrationId}/confirm`);
      success('Registration confirmed successfully');
      fetchRegistrations();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to confirm registration');
    }
  };

  const handleMarkAttended = async (registrationId: string) => {
    try {
      await api.post(`/admin/registrations/${registrationId}/mark-attended`);
      success('Registration marked as attended successfully');
      fetchRegistrations();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to mark registration as attended');
    }
  };

  const handleRejectRegistration = (registration: EventRegistration) => {
    const userName = registration.user ? 
      `${registration.user.firstName} ${registration.user.lastName}` : 
      registration.guestUser ? 
        `${registration.guestUser.firstName} ${registration.guestUser.lastName} (Guest)` : 
        'Unknown User';
    
    setRegistrationToReject({ id: registration._id, name: userName });
    setShowRejectRegistrationDialog(true);
  };

  const handleDeleteRegistration = (registration: EventRegistration) => {
    const userName = registration.user ? 
      `${registration.user.firstName} ${registration.user.lastName}` : 
      registration.guestUser ? 
        `${registration.guestUser.firstName} ${registration.guestUser.lastName} (Guest)` : 
        'Unknown User';
    
    setRegistrationToDelete({ id: registration._id, name: userName });
    setShowDeleteRegistrationDialog(true);
  };

  const confirmRejectRegistration = async () => {
    if (!registrationToReject) return;
    
    try {
      await api.post(`/registrations/${registrationToReject.id}/reject`);
      success('Registration rejected successfully');
      setShowRejectRegistrationDialog(false);
      setRegistrationToReject(null);
      fetchRegistrations();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to reject registration');
    }
  };

  const confirmDeleteRegistration = async () => {
    if (!registrationToDelete) return;
    
    try {
      await api.delete(`/registrations/${registrationToDelete.id}`);
      success('Registration deleted successfully');
      setShowDeleteRegistrationDialog(false);
      setRegistrationToDelete(null);
      fetchRegistrations();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to delete registration');
    }
  };

  const handleUserClick = (registration: EventRegistration) => {
    if (registration.user) {
      // Registered user - show profile modal
      setSelectedUserId(registration.user._id);
      setShowUserProfileModal(true);
    } else if (registration.guestUser) {
      // Guest user - show guest details modal
      setSelectedGuestData({
        ...registration.guestUser,
        registrationDate: registration.registrationDate,
        eventTitle: registration.event?.title,
        hackathonTitle: registration.hackathon?.title
      });
      setShowGuestDetailsModal(true);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedRegistrations.length === 0) return;
    
    try {
      if (bulkAction === 'approve') {
        await Promise.all(
          selectedRegistrations.map(id => api.post(`/registrations/${id}/confirm`))
        );
        success(`${selectedRegistrations.length} registrations approved successfully`);
      } else if (bulkAction === 'reject') {
        await Promise.all(
          selectedRegistrations.map(id => api.post(`/registrations/${id}/reject`))
        );
        success(`${selectedRegistrations.length} registrations rejected successfully`);
      }
      
      setSelectedRegistrations([]);
      setBulkAction('');
      fetchRegistrations();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to perform bulk action');
    }
  };

  const handleSelectAllRegistrations = () => {
    if (selectedRegistrations.length === registrations.length) {
      setSelectedRegistrations([]);
    } else {
      setSelectedRegistrations(registrations.map(r => r._id));
    }
  };

  const handleSelectRegistration = (registrationId: string) => {
    setSelectedRegistrations(prev => 
      prev.includes(registrationId) 
        ? prev.filter(id => id !== registrationId)
        : [...prev, registrationId]
    );
  };

  const formatDateDisplay = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTimeDisplay = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getEventStatus = (event: EventWithHackathon) => {
    // First check if event is active
    if (!event.isActive) return 'inactive';
    
    const now = new Date();
    const startDate = new Date(event.startTime);
    const endDate = new Date(event.endTime);
    
    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'ongoing';
    return 'past';
  };

  const filteredEvents = events.filter(event => {
    const matchesType = filterType === 'all' || event.eventType === filterType;
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Event Management</h1>
          <p className="text-gray-600">Manage workshops, seminars, competitions, and registrations</p>
        </div>
        {activeTab === 'events' && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('events')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'events'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Calendar className="h-4 w-4 inline mr-2" />
            Events
          </button>
          <button
            onClick={() => setActiveTab('registrations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'registrations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserCheck className="h-4 w-4 inline mr-2" />
            Registrations
          </button>
          <button
            onClick={() => setActiveTab('responses')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'responses'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ClipboardList className="h-4 w-4 inline mr-2" />
            Responses
          </button>
          <button
            onClick={() => setActiveTab('teams')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'teams'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Form Teams
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <IndianRupee className="h-4 w-4 inline mr-2" />
            Payments
          </button>
          <button
            onClick={() => setActiveTab('emails')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'emails'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Mail className="h-4 w-4 inline mr-2" />
            Email
          </button>
        </nav>
      </div>

      {activeTab === 'teams' ? (
        <FormTeamPanel />
      ) : activeTab === 'responses' ? (
        <ResponsesPanel events={events.map((e) => ({ _id: e._id, title: e.title }))} />
      ) : activeTab === 'emails' ? (
        <EmailPanel events={events.map((e) => ({ _id: e._id, title: e.title }))} />
      ) : activeTab === 'payments' ? (
        <PaymentsPanel events={events.map((e) => ({ _id: e._id, title: e.title }))} />
      ) : activeTab === 'events' ? (
        <>
          {/* Events Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="hackathon">Hackathons</option>
                  <option value="workshop">Workshops</option>
                  <option value="seminar">Seminars</option>
                  <option value="competition">Competitions</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Events Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEvents.map((event) => (
          <Card key={event._id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {event.eventType === 'hackathon' ? (
                    <Trophy className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <Calendar className="h-5 w-5 text-blue-600" />
                  )}
                  <CardTitle className="text-lg">{event.title}</CardTitle>
                </div>
                <StatusBadge status={getEventStatus(event)} />
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="capitalize">{event.eventType}</span>
                {event.requiresAdminApproval && (
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
                    Approval Required
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>{formatDateDisplay(event.startTime)} - {formatDateDisplay(event.endTime)}</span>
                </div>
                
                {event.location && (
                  <div className="flex items-center text-sm text-gray-500">
                    <span>📍 {event.location}</span>
                  </div>
                )}
                
                {event.eventType === 'hackathon' && event.hackathon ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="h-4 w-4 mr-2" />
                    <span>{event.hackathon.currentTeams}/{event.hackathon.maxTeams} teams</span>
                  </div>
                ) : event.maxParticipants && (user?.role === 'admin' || event.showParticipantCount !== false) ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="h-4 w-4 mr-2" />
                    <span>{event.currentParticipants || 0}/{event.maxParticipants} participants</span>
                  </div>
                ) : null}
                
                {event.registrationDeadline && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>Deadline: {formatDateDisplay(event.registrationDeadline)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center">
                <div className="text-xs text-gray-500">
                  Created: {event.createdAt ? formatDateDisplay(event.createdAt) : 'N/A'}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowViewModal(true);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowEditModal(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleEventStatus(event._id)}
                  >
                    {event.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteEvent(event._id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

          {filteredEvents.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
              <p className="text-gray-600">
                {searchTerm || filterType !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Create your first event to get started'
                }
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Registrations Filters & Stats */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            {/* Counts Display */}
            {/* Adding somebody who did not register themselves — they paid in
                person, or asked at a stall. Needs an event chosen, so it knows
                which list to add them to. */}
            {registrationFilters.eventId && (
              <div className="mb-6">
                <AddParticipant
                  eventId={registrationFilters.eventId}
                  onAdded={() => { fetchRegistrations(); fetchRegistrationCounts(); }}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{registrationCounts.total}</div>
                <div className="text-sm text-blue-800">Total Registrations</div>
              </div>
              {registrationCounts.byStatus.map((statusCount) => (
                <div key={statusCount._id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-gray-700">{statusCount.count}</div>
                  <div className="text-sm text-gray-600 capitalize">
                    {statusCount._id === 'registered' ? 'Pending' : statusCount._id}
                  </div>
                </div>
              ))}
            </div>

            {/* Filters & Export */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search by user name or email..."
                    value={registrationFilters.searchTerm}
                    onChange={(e) => setRegistrationFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <select
                    value={registrationFilters.eventId}
                    onChange={(e) => setRegistrationFilters(prev => ({ ...prev, eventId: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Events</option>
                    {events.map(event => (
                      <option key={event._id} value={event._id}>{event.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={registrationFilters.status}
                    onChange={(e) => setRegistrationFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="registered">Pending Approval</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="attended">Attended</option>
                    <option value="withdrawn">Withdrawn</option>
                  </select>
                </div>
              </div>
              
              {/* Export Button */}
              <div className="flex gap-2">
                <Button
                  onClick={() => exportRegistrations()}
                  disabled={exportLoading || registrations.length === 0}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exportLoading ? 'Exporting...' : 'Export to Excel'}
                </Button>
              </div>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedRegistrations.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedRegistrations.length} registration(s) selected
                  </span>
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value as 'approve' | 'reject' | '')}
                    className="px-3 py-1 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Action</option>
                    <option value="approve">Approve Selected</option>
                  </select>
                  <Button
                    onClick={handleBulkAction}
                    disabled={!bulkAction}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Apply
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedRegistrations([])}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}

          {/* Registrations Table */}
          {registrationsLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedRegistrations.length === registrations.length && registrations.length > 0}
                          onChange={handleSelectAllRegistrations}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event/Hackathon
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registration Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {registrations.map((registration) => (
                      <tr key={registration._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRegistrations.includes(registration._id)}
                            onChange={() => handleSelectRegistration(registration._id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div 
                              className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                              onClick={() => handleUserClick(registration)}
                            >
                              {registration.user ? (
                                `${registration.user.firstName} ${registration.user.lastName}`
                              ) : registration.guestUser ? (
                                `${registration.guestUser.firstName} ${registration.guestUser.lastName} (Guest)`
                              ) : 'Unknown User'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {registration.user?.email || registration.guestUser?.email || 'No email'}
                            </div>
                            {(registration.user?.phone || registration.guestUser?.phone) && (
                              <div className="text-sm text-gray-500">
                                {registration.user?.phone || registration.guestUser?.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            registration.userType === 'guest' 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {registration.userType === 'guest' ? 'Guest' : 'Registered'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {registration.event?.title || registration.hackathon?.title || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {registration.event ? (
                                `${formatDateDisplay(registration.event.startTime)} - ${formatDateDisplay(registration.event.endTime)}`
                              ) : registration.hackathon ? (
                                `${formatDateDisplay(registration.hackathon.startDate)} - ${formatDateDisplay(registration.hackathon.endDate)}`
                              ) : 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateTimeDisplay(registration.registrationDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={registration.status}
                            onChange={(e) => handleChangeStatus(registration, e.target.value)}
                            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                            title="Change this registration's status"
                          >
                            {REGISTRATION_STATUSES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {/* Delete Button */}
                            <Button
                              onClick={() => handleDeleteRegistration(registration)}
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-600 hover:bg-red-50"
                              title="Delete Registration"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {registrations.length === 0 && (
                <div className="text-center py-12">
                  <UserCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No registrations found</h3>
                  <p className="text-gray-600">
                    {registrationFilters.searchTerm || registrationFilters.eventId || registrationFilters.status !== 'all'
                      ? 'Try adjusting your filters'
                      : 'No registrations have been made yet'
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchEvents();
          }}
        />
      )}

      {/* Edit Event Modal */}
      {showEditModal && selectedEvent && (
        <EditEventModal
          event={selectedEvent}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEvent(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedEvent(null);
            fetchEvents();
          }}
        />
      )}

      {/* View Event Modal */}
      {showViewModal && selectedEvent && (
        <ViewEventModal
          event={selectedEvent}
          onClose={() => {
            setShowViewModal(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setEventToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteEvent}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Registration Confirmation Dialog */}
      <Dialog open={showRejectRegistrationDialog} onOpenChange={setShowRejectRegistrationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Registration</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject the registration for "{registrationToReject?.name}"? This will change the status to "withdrawn".
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectRegistrationDialog(false);
                setRegistrationToReject(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRejectRegistration}
            >
              Reject Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Registration Confirmation Dialog */}
      <Dialog open={showDeleteRegistrationDialog} onOpenChange={setShowDeleteRegistrationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Registration</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete the registration for "{registrationToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteRegistrationDialog(false);
                setRegistrationToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteRegistration}
            >
              Delete Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showUserProfileModal}
        onClose={() => {
          setShowUserProfileModal(false);
          setSelectedUserId('');
        }}
        userId={selectedUserId}
      />

      {/* Guest Details Modal */}
      <GuestDetailsModal
        isOpen={showGuestDetailsModal}
        onClose={() => {
          setShowGuestDetailsModal(false);
          setSelectedGuestData(null);
        }}
        guestData={selectedGuestData}
        registrationDate={selectedGuestData?.registrationDate || ''}
        eventTitle={selectedGuestData?.eventTitle}
        hackathonTitle={selectedGuestData?.hackathonTitle}
      />
    </div>
  );
};

// Placeholder components for modals - these would be implemented separately
const CreateEventModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Workshop',
    startTime: '',
    endTime: '',
    location: '',
    isVirtual: false,
    meetingLink: '',
    capacity: '', // Added required field
    maxParticipants: '',
    registrationRequired: false,
    registrationType: 'mandatory_signup' as 'mandatory_signup' | 'optional_signup',
    attendanceMode: 'registration' as 'registration' | 'rsvp',
    // Kept equal to category so the public events page shows the right type.
    eventType: 'workshop',
    // One shared shape, so adding a field type does not mean editing this too.
    guestFormFields: [] as CustomQuestionField[],
    requiresAdminApproval: false,
    showParticipantCount: true,
    isPaidEvent: false,
    eventFee: '',
    registrationDeadline: '',
    communityInviteUrl: '',
    hackathon: '',
    speakers: [] as Speaker[],
    eventFlyer: null as File | null,
    showInHomeCarousel: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { success, error: showError } = useToast();

  // Autosaved locally, so a validation failure or a closed tab does not cost the
  // whole form. Cleared once the event actually saves.
  const draft = useFormDraft('new', formData, (saved) => setFormData((prev) => ({ ...prev, ...saved })));

  // Fills the form from a pasted brief. Only sets what was actually recognised,
  // so a partial brief never blanks fields the admin already typed.
  const applyBrief = (parsed: ParsedBrief) => {
    const ev = parsed.event;
    setFormData((prev) => ({
      ...prev,
      ...(ev.title !== undefined && { title: ev.title }),
      ...(ev.description !== undefined && { description: ev.description }),
      // Both, always: admin screens group by category while the public events
      // page shows eventType. Setting only one makes the event read as "other".
      ...(ev.category !== undefined && { category: ev.category, eventType: ev.category }),
      ...(ev.startTime !== undefined && { startTime: ev.startTime }),
      ...(ev.endTime !== undefined && { endTime: ev.endTime }),
      ...(ev.location !== undefined && { location: ev.location }),
      ...(ev.isVirtual !== undefined && { isVirtual: ev.isVirtual }),
      ...(ev.meetingLink !== undefined && { meetingLink: ev.meetingLink }),
      ...(ev.capacity !== undefined && { capacity: ev.capacity }),
      ...(ev.registrationDeadline !== undefined && { registrationDeadline: ev.registrationDeadline }),
      ...(ev.attendanceMode !== undefined && { attendanceMode: ev.attendanceMode }),
      // Questions imply collecting answers, so registration is switched on. Who
      // may answer them is left to whatever the admin chose - members and
      // guests can both be asked.
      ...(parsed.fields.length > 0 && {
        registrationRequired: true,
        guestFormFields: parsed.fields,
      }),
      ...(parsed.fields.length === 0 && ev.attendanceMode === 'rsvp' && {
        registrationRequired: true,
      }),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Checked here so a problem is named in terms of the question on screen,
    // rather than coming back from the database as an index counted from zero.
    const questionProblems = validateFieldDefinitions(formData.guestFormFields || []);
    if (questionProblems.length > 0) {
      setError(questionProblems.join('\n'));
      setLoading(false);
      return;
    }

    try {
      // Validate speakers - name is required if speakers are added
      const invalidSpeakers = formData.speakers.filter(speaker => !speaker.name.trim());
      if (invalidSpeakers.length > 0) {
        setError('Speaker name is required for all speakers');
        setLoading(false);
        return;
      }

      // Validate paid event fee
      if (formData.isPaidEvent && (!formData.eventFee || parseFloat(String(formData.eventFee)) <= 0)) {
        setError('Paid events must have a fee greater than 0');
        setLoading(false);
        return;
      }

      // Upload speaker pictures first
      const processedSpeakers = await Promise.all(
        formData.speakers.map(async (speaker) => {
          if (speaker.picture) {
            const uploadResult = await uploadAPI.uploadSpeakerPicture(speaker.picture);
            const { picture, ...speakerWithoutFile } = speaker; // Destructure to remove File object
            return { ...speakerWithoutFile, picture: uploadResult.data.pictureUrl };
          }
          const { picture, ...speakerWithoutFile } = speaker; // Destructure to remove File object
          return { ...speakerWithoutFile, picture: null }; // No picture uploaded
        })
      );

      // Upload event flyer if provided
      let eventFlyerUrl = null;
      if (formData.eventFlyer) {
        const flyerUploadResult = await uploadAPI.uploadEventFlyer(formData.eventFlyer);
        eventFlyerUrl = flyerUploadResult.data.flyerUrl;
      }

      const eventData = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : 100, // Default capacity
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : undefined,
        registrationDeadline: formData.registrationDeadline || undefined,
        communityInviteUrl: formData.communityInviteUrl?.trim() || '',
        ...(formData.hackathon ? { hackathon: formData.hackathon } : {}),
        startTime: new Date(formData.startTime), // Convert to Date object
        endTime: new Date(formData.endTime), // Convert to Date object
        meetingLink: formData.isVirtual ? formData.meetingLink : undefined,
        speakers: processedSpeakers,
        eventFlyer: eventFlyerUrl,
        isPaidEvent: formData.isPaidEvent,
        eventFee: formData.isPaidEvent ? parseFloat(String(formData.eventFee) || '0') : 0
      };

      const response = await api.post('/events', eventData);
      
      if (response.data) {
        success('Event created successfully!');
        // Saved for real, so the local copy is no longer needed.
        draft.clearDraft();
        onSuccess();
      } else {
        setError(response.data.message || 'Failed to create event');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create event';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addSpeaker = () => {
    const newSpeaker: Speaker = {
      id: Date.now().toString(),
      salutation: '',
      name: '',
      bio: '',
      achievements: '',
      picture: null,
      topic: ''
    };
    setFormData(prev => ({
      ...prev,
      speakers: [...prev.speakers, newSpeaker]
    }));
  };

  const updateSpeaker = (id: string, field: keyof Speaker, value: string | File | null) => {
    setFormData(prev => ({
      ...prev,
      speakers: prev.speakers.map(speaker =>
        speaker.id === id ? { ...speaker, [field]: value } : speaker
      )
    }));
  };

  const removeSpeaker = (id: string) => {
    setFormData(prev => ({
      ...prev,
      speakers: prev.speakers.filter(speaker => speaker.id !== id)
    }));
  };

  const handleSpeakerPictureChange = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file for speaker picture');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Speaker picture must be less than 10MB');
        return;
      }
      updateSpeaker(id, 'picture', file);
      if (error) setError('');
    }
  };

  const handleFlyerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF or image file for event flyer');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Event flyer must be less than 10MB');
        return;
      }
      setFormData(prev => ({ ...prev, eventFlyer: file }));
      if (error) setError('');
    }
  };

  const handleRemoveFlyer = () => {
    setFormData(prev => ({ 
      ...prev, 
      eventFlyer: null,
      existingEventFlyer: '' 
    }));
    success('Event flyer removed');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-hidden">
      {/* Full width and height: building a twenty-question form in a small
          dialogue meant constant scrolling, with the questions squeezed into a
          narrow column. */}
      <div className="bg-white w-full h-full max-w-none overflow-y-auto p-6 md:p-10">
        <div className="mx-auto w-full max-w-5xl">
        <h2 className="text-2xl font-bold mb-4">Create New Event</h2>

        <DraftBanner
          hasDraft={draft.hasDraft}
          draftSavedAt={draft.draftSavedAt}
          lastSavedAt={draft.lastSavedAt}
          onRestore={draft.restoreDraft}
          onDiscard={draft.discardDraft}
        />

        <EventBriefImporter onApply={applyBrief} />

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Event Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter event title"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter event description"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Event Type *
              </label>
              {/* Sets category and eventType together: admin screens group by
                  category, members see and filter by eventType. */}
              <EventTypePicker
                id="category"
                value={formData.category}
                onChange={(slug) =>
                  setFormData((prev) => ({ ...prev, category: slug, eventType: slug }))
                }
              />
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                Location *
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter event location"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isVirtual"
                name="isVirtual"
                checked={formData.isVirtual}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isVirtual" className="ml-2 block text-sm text-gray-900">
                Virtual Event
              </label>
            </div>

            {formData.isVirtual && (
              <div>
                <label htmlFor="meetingLink" className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Link (Optional)
                </label>
                <input
                  type="url"
                  id="meetingLink"
                  name="meetingLink"
                  value={formData.meetingLink}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://meet.google.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 You can leave this empty and update it later after creating the meeting link
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
                Capacity *
              </label>
              <input
                type="number"
                id="capacity"
                name="capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                required
                min="1"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter event capacity"
              />
            </div>

            <div>
              <label htmlFor="maxParticipants" className="block text-sm font-medium text-gray-700 mb-1">
                Max Participants
              </label>
              <input
                type="number"
                id="maxParticipants"
                name="maxParticipants"
                value={formData.maxParticipants}
                onChange={handleInputChange}
                min="1"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter max participants"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showParticipantCount"
                name="showParticipantCount"
                checked={formData.showParticipantCount}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="showParticipantCount" className="ml-2 block text-sm text-gray-900">
                Show to Participants
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date & Time *
              </label>
              <input
                type="datetime-local"
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
                End Date & Time *
              </label>
              <input
                type="datetime-local"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="registrationDeadline" className="block text-sm font-medium text-gray-700 mb-1">
                Registration Deadline
              </label>
              <input
                type="datetime-local"
                id="registrationDeadline"
                name="registrationDeadline"
                value={formData.registrationDeadline}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="registrationRequired"
                name="registrationRequired"
                checked={formData.registrationRequired}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="registrationRequired" className="ml-2 block text-sm text-gray-900">
                Registration Required
              </label>
            </div>

            
            {formData.registrationRequired && (
              <div className="ml-6 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How do people confirm they're coming?
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="attendanceMode_registration"
                        name="attendanceMode"
                        value="registration"
                        checked={formData.attendanceMode !== 'rsvp'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="attendanceMode_registration" className="ml-2 block text-sm text-gray-900">
                        Full registration form — for hackathons and workshops
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="attendanceMode_rsvp"
                        name="attendanceMode"
                        value="rsvp"
                        checked={formData.attendanceMode === 'rsvp'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="attendanceMode_rsvp" className="ml-2 block text-sm text-gray-900">
                        Quick RSVP (going / maybe / can't go) — for conclaves and meetups
                      </label>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    RSVP is one tap for signed-in members. The registration form collects full details and supports guests.
                  </p>
                </div>

                <div className={formData.attendanceMode === 'rsvp' ? 'opacity-50 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Type
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="registrationType_mandatory"
                        name="registrationType"
                        value="mandatory_signup"
                        checked={formData.registrationType === 'mandatory_signup'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="registrationType_mandatory" className="ml-2 block text-sm text-gray-900">
                        Registration Required (Mandatory Signup/Signin with UP ID)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="registrationType_optional"
                        name="registrationType"
                        value="optional_signup"
                        checked={formData.registrationType === 'optional_signup'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="registrationType_optional" className="ml-2 block text-sm text-gray-900">
                        Registration Required (No Mandatory Sign up with UP ID)
                      </label>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose whether users must have a Udyogapramoda account or can register as guests.
                  </p>
                </div>

                {/* Only meaningful for a hackathon; an ordinary event has no teams. */}
                {formData.eventType === 'hackathon' && (
                  <HackathonLinkField
                    value={formData.hackathon}
                    onChange={(id) => setFormData(prev => ({ ...prev, hackathon: id }))}
                    eventId={undefined}
                  />
                )}

                <div className="border-t pt-4">
                  <label htmlFor="communityInviteUrl" className="block text-sm font-medium text-gray-700 mb-1">
                    Group link for this event
                  </label>
                  <input
                    type="url"
                    id="communityInviteUrl"
                    name="communityInviteUrl"
                    value={formData.communityInviteUrl}
                    onChange={handleInputChange}
                    placeholder="https://chat.whatsapp.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Sent in the confirmation email and shown after registering. A WhatsApp or
                    Discord invite. Leave blank to use the community link from Site Settings.
                  </p>
                </div>

                {/* Questions are asked either way. Members answer them alongside
                    the details already on their profile; guests answer them as
                    part of the guest form. */}
                <div className="border-t pt-4">
                  <CustomFormFieldsManager
                    fields={formData.guestFormFields || []}
                    onChange={(fields) => setFormData(prev => ({ ...prev, guestFormFields: fields }))}
                  />
                </div>
              </div>
            )}<div className="flex items-center">
              <input
                type="checkbox"
                id="requiresAdminApproval"
                name="requiresAdminApproval"
                checked={formData.requiresAdminApproval}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="requiresAdminApproval" className="ml-2 block text-sm text-gray-900">
                Approve each registration myself
                <span className="block text-xs font-normal text-gray-500">
                  People who register wait as pending until you approve them on the
                  Registrations tab. Leave unticked to confirm everyone automatically.
                </span>
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPaidEvent"
                name="isPaidEvent"
                checked={formData.isPaidEvent}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isPaidEvent" className="ml-2 block text-sm text-gray-900">
                Paid Event
              </label>
            </div>

            {formData.isPaidEvent && (
              <div>
                <label htmlFor="eventFee" className="block text-sm font-medium text-gray-700 mb-1">
                  Event Fee (₹) *
                </label>
                <input
                  type="number"
                  id="eventFee"
                  name="eventFee"
                  value={formData.eventFee}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  required={formData.isPaidEvent}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter event fee amount"
                />
                <p className="text-sm text-gray-500 mt-1">Enter 0 for free events that require registration</p>
              </div>
            )}
          </div>

          {/* Speakers Section */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Event Speakers</h3>
              <Button type="button" onClick={addSpeaker} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Speaker
              </Button>
            </div>
            
            {formData.speakers.map((speaker, index) => (
              <div key={speaker.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Speaker {index + 1}</h4>
                  <Button 
                    type="button" 
                    onClick={() => removeSpeaker(speaker.id)} 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salutation
                    </label>
                    <input
                      type="text"
                      value={speaker.salutation}
                      onChange={(e) => updateSpeaker(speaker.id, 'salutation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Dr., Mr., Ms., etc."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={speaker.name}
                      onChange={(e) => updateSpeaker(speaker.id, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Speaker name"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bio
                    </label>
                    <textarea
                      value={speaker.bio}
                      onChange={(e) => updateSpeaker(speaker.id, 'bio', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Speaker biography"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Achievements
                    </label>
                    <textarea
                      value={speaker.achievements}
                      onChange={(e) => updateSpeaker(speaker.id, 'achievements', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Speaker achievements and credentials"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Picture
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleSpeakerPictureChange(speaker.id, e)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {speaker.picture && (
                      <div className="mt-2">
                        <p className="text-sm text-green-600 mb-2">✓ {speaker.picture.name}</p>
                        <img
                          src={URL.createObjectURL(speaker.picture)}
                          alt="Speaker preview"
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Topic
                    </label>
                    <input
                      type="text"
                      value={speaker.topic}
                      onChange={(e) => updateSpeaker(speaker.id, 'topic', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Speaking topic"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Event Flyer Section */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-lg font-medium text-gray-900">Event Flyer</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Flyer (PDF or Image - Max 10MB)
              </label>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={handleFlyerChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formData.eventFlyer && (
                <div className="mt-2">
                  <p className="text-sm text-green-600 mb-2">✓ {formData.eventFlyer.name}</p>
                  {formData.eventFlyer.type.includes('pdf') ? (
                    <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center border-2 border-gray-200">
                      <span className="text-red-600 font-bold text-xs">PDF</span>
                    </div>
                  ) : (
                    <img
                      src={URL.createObjectURL(formData.eventFlyer)}
                      alt="Flyer preview"
                      className="w-16 h-16 rounded-lg object-cover border-2 border-gray-200"
                    />
                  )}
                  <button
                    type="button"
                    onClick={handleRemoveFlyer}
                    className="text-red-600 hover:text-red-700 text-sm mt-2 underline"
                  >
                    Remove Flyer
                  </button>
                </div>
              )}
            </div>
            
            {/* Show in Home Carousel Checkbox */}
            <div className="mt-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="showInHomeCarousel"
                  checked={formData.showInHomeCarousel}
                  onChange={handleInputChange}
                  disabled={!formData.eventFlyer}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                />
                <span className={`text-sm font-medium ${!formData.eventFlyer ? 'text-gray-400' : 'text-gray-700'}`}>
                  Show event flyer in home page carousel
                  {!formData.eventFlyer && (
                    <span className="text-xs text-gray-400 block">
                      (Upload a flyer first to enable this option)
                    </span>
                  )}
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Check this to display the event flyer in the home page carousel (only if flyer is uploaded)
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Event'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

const EditEventModal: React.FC<{ event: EventWithHackathon; onClose: () => void; onSuccess: () => void }> = ({ event, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: event.title,
    description: event.description,
    category: event.category || 'workshop',
    eventType: event.eventType || 'workshop',
    startTime: formatDateTimeLocal(event.startTime), // Format for datetime-local input in IST
    endTime: formatDateTimeLocal(event.endTime),
    location: event.location || '',
    isVirtual: event.isVirtual || false,
    meetingLink: event.meetingLink || '',
    capacity: event.capacity?.toString() || '100',
    maxParticipants: event.maxParticipants?.toString() || '',
    registrationRequired: event.registrationRequired || false,
    registrationType: event.registrationType || 'mandatory_signup',
    attendanceMode: event.attendanceMode || 'registration',
    guestFormFields: event.guestFormFields || [],
    requiresAdminApproval: event.requiresAdminApproval || false,
    showParticipantCount: event.showParticipantCount !== false, // Default to true if not set
    isPaidEvent: event.isPaidEvent || false,
    eventFee: event.eventFee || '',
    registrationDeadline: event.registrationDeadline ? formatDateTimeLocal(event.registrationDeadline) : '',
    communityInviteUrl: (event as any).communityInviteUrl || '',
    hackathon: (event as any).hackathon?._id || (event as any).hackathon || '',
    speakers: (event.speakers || []).map((speaker: any, index: number) => ({
      id: speaker.id || `existing-${index}`,
      salutation: speaker.salutation || '',
      name: speaker.name || '',
      bio: speaker.bio || '',
      achievements: speaker.achievements || '',
      picture: null, // Will be handled separately for existing files
      topic: speaker.topic || ''
    })) as Speaker[],
    eventFlyer: null as File | null,
    showInHomeCarousel: event.showInHomeCarousel || false,
    existingSpeakerPictures: (event.speakers || []).reduce((acc: any, speaker: any, index: number) => {
      const id = speaker.id || `existing-${index}`;
      if (speaker.picture) acc[id] = speaker.picture;
      return acc;
    }, {}),
    existingEventFlyer: event.eventFlyer || null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { success, error: showError } = useToast();

  const draft = useFormDraft(`event:${event._id}`, formData, (saved) => setFormData((prev) => ({ ...prev, ...saved })));

  /**
   * Fills this event from a pasted brief.
   *
   * More cautious than the create version: only fields the parser actually
   * recognised are written, so a partial brief cannot blank details already
   * entered. Replacing the questions is asked about separately, because answers
   * already collected are filed against them.
   */
  const applyBrief = (parsed: ParsedBrief) => {
    const ev = parsed.event;

    let replaceQuestions = parsed.fields.length > 0;
    if (replaceQuestions && (formData.guestFormFields || []).length > 0) {
      replaceQuestions = window.confirm(
        `This event already has ${formData.guestFormFields.length} question(s).\n\n` +
        `Replace them with the ${parsed.fields.length} from the brief?\n\n` +
        `OK replaces them. Cancel keeps the existing questions and applies only the event details.`
      );
    }

    setFormData((prev) => ({
      ...prev,
      ...(ev.title !== undefined && { title: ev.title }),
      ...(ev.description !== undefined && { description: ev.description }),
      ...(ev.category !== undefined && { category: ev.category, eventType: ev.category }),
      ...(ev.startTime !== undefined && { startTime: ev.startTime }),
      ...(ev.endTime !== undefined && { endTime: ev.endTime }),
      ...(ev.location !== undefined && { location: ev.location }),
      ...(ev.isVirtual !== undefined && { isVirtual: ev.isVirtual }),
      ...(ev.meetingLink !== undefined && { meetingLink: ev.meetingLink }),
      ...(ev.capacity !== undefined && { capacity: ev.capacity }),
      ...(ev.registrationDeadline !== undefined && { registrationDeadline: ev.registrationDeadline }),
      ...(ev.attendanceMode !== undefined && { attendanceMode: ev.attendanceMode }),
      ...(replaceQuestions && {
        registrationRequired: true,
        guestFormFields: parsed.fields,
      }),
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Checked here so a problem is named in terms of the question on screen,
    // rather than coming back from the database as an index counted from zero.
    const questionProblems = validateFieldDefinitions(formData.guestFormFields || []);
    if (questionProblems.length > 0) {
      setError(questionProblems.join('\n'));
      setLoading(false);
      return;
    }

    try {
      // Validate speakers - name is required if speakers are added
      const invalidSpeakers = formData.speakers.filter(speaker => !speaker.name.trim());
      if (invalidSpeakers.length > 0) {
        setError('Speaker name is required for all speakers');
        setLoading(false);
        return;
      }

      // Validate paid event fee
      if (formData.isPaidEvent && (!formData.eventFee || parseFloat(String(formData.eventFee)) <= 0)) {
        setError('Paid events must have a fee greater than 0');
        setLoading(false);
        return;
      }

      // Upload new speaker pictures and keep existing ones
      const processedSpeakers = await Promise.all(
        formData.speakers.map(async (speaker) => {
          if (speaker.picture) {
            const uploadResult = await uploadAPI.uploadSpeakerPicture(speaker.picture);
            const { picture, ...speakerWithoutFile } = speaker;
            return { ...speakerWithoutFile, picture: uploadResult.data.pictureUrl };
          }
          const { picture, ...speakerWithoutFile } = speaker;
          return { ...speakerWithoutFile, picture: formData.existingSpeakerPictures[speaker.id] || null };
        })
      );

      // Upload new event flyer or keep existing one
      let eventFlyerUrl = formData.existingEventFlyer;
      if (formData.eventFlyer) {
        const flyerUploadResult = await uploadAPI.uploadEventFlyer(formData.eventFlyer);
        eventFlyerUrl = flyerUploadResult.data.flyerUrl;
      }

      const eventData = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : 100,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : undefined,
        registrationDeadline: formData.registrationDeadline || undefined,
        communityInviteUrl: formData.communityInviteUrl?.trim() || '',
        ...(formData.hackathon ? { hackathon: formData.hackathon } : {}),
        startTime: new Date(formData.startTime),
        endTime: new Date(formData.endTime),
        meetingLink: formData.isVirtual ? formData.meetingLink : undefined,
        speakers: processedSpeakers,
        eventFlyer: eventFlyerUrl,
        isPaidEvent: formData.isPaidEvent,
        eventFee: formData.isPaidEvent ? parseFloat(String(formData.eventFee) || '0') : 0
      };

      const response = await api.put(`/events/${event._id}`, eventData);
      
      if (response.data) {
        success('Event updated successfully!');
        // Saved for real, so the local copy is no longer needed.
        draft.clearDraft();
        onSuccess();
      } else {
        setError(response.data.message || 'Failed to update event');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update event';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addSpeaker = () => {
    const newSpeaker: Speaker = {
      id: Date.now().toString(),
      salutation: '',
      name: '',
      bio: '',
      achievements: '',
      picture: null,
      topic: ''
    };
    setFormData(prev => ({
      ...prev,
      speakers: [...prev.speakers, newSpeaker]
    }));
  };

  const updateSpeaker = (id: string, field: keyof Speaker, value: string | File | null) => {
    setFormData(prev => ({
      ...prev,
      speakers: prev.speakers.map(speaker =>
        speaker.id === id ? { ...speaker, [field]: value } : speaker
      )
    }));
  };

  const removeSpeaker = (id: string) => {
    setFormData(prev => ({
      ...prev,
      speakers: prev.speakers.filter(speaker => speaker.id !== id)
    }));
  };

  const handleSpeakerPictureChange = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file for speaker picture');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Speaker picture must be less than 10MB');
        return;
      }
      updateSpeaker(id, 'picture', file);
      if (error) setError('');
    }
  };

  const handleFlyerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF or image file for event flyer');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Event flyer must be less than 10MB');
        return;
      }
      setFormData(prev => ({ ...prev, eventFlyer: file }));
      if (error) setError('');
    }
  };

  const handleRemoveFlyer = () => {
    setFormData(prev => ({ 
      ...prev, 
      eventFlyer: null,
      existingEventFlyer: '' 
    }));
    success('Event flyer removed');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-hidden">
      {/* Full width and height: building a twenty-question form in a small
          dialogue meant constant scrolling, with the questions squeezed into a
          narrow column. */}
      <div className="bg-white w-full h-full max-w-none overflow-y-auto p-6 md:p-10">
        <div className="mx-auto w-full max-w-5xl">
        <h2 className="text-2xl font-bold mb-4">Edit Event: {event.title}</h2>

        <DraftBanner
          hasDraft={draft.hasDraft}
          draftSavedAt={draft.draftSavedAt}
          lastSavedAt={draft.lastSavedAt}
          onRestore={draft.restoreDraft}
          onDiscard={draft.discardDraft}
        />

        <EventBriefImporter onApply={applyBrief} mode="edit" />
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Event Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter event title"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter event description"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Event Type *
              </label>
              {/* Sets category and eventType together: admin screens group by
                  category, members see and filter by eventType. */}
              <EventTypePicker
                id="category"
                value={formData.category}
                onChange={(slug) =>
                  setFormData((prev) => ({ ...prev, category: slug, eventType: slug }))
                }
              />
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                Location *
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter event location"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isVirtual"
                name="isVirtual"
                checked={formData.isVirtual}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isVirtual" className="ml-2 block text-sm text-gray-900">
                Virtual Event
              </label>
            </div>

            {formData.isVirtual && (
              <div>
                <label htmlFor="meetingLink" className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Link (Optional)
                </label>
                <input
                  type="url"
                  id="meetingLink"
                  name="meetingLink"
                  value={formData.meetingLink}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://meet.google.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 You can leave this empty and update it later after creating the meeting link
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
                Capacity *
              </label>
              <input
                type="number"
                id="capacity"
                name="capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                required
                min="1"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter event capacity"
              />
            </div>

            <div>
              <label htmlFor="maxParticipants" className="block text-sm font-medium text-gray-700 mb-1">
                Max Participants
              </label>
              <input
                type="number"
                id="maxParticipants"
                name="maxParticipants"
                value={formData.maxParticipants}
                onChange={handleInputChange}
                min="1"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter max participants"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showParticipantCount"
                name="showParticipantCount"
                checked={formData.showParticipantCount}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="showParticipantCount" className="ml-2 block text-sm text-gray-900">
                Show to Participants
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date & Time *
              </label>
              <input
                type="datetime-local"
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
                End Date & Time *
              </label>
              <input
                type="datetime-local"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="registrationDeadline" className="block text-sm font-medium text-gray-700 mb-1">
                Registration Deadline
              </label>
              <input
                type="datetime-local"
                id="registrationDeadline"
                name="registrationDeadline"
                value={formData.registrationDeadline}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="registrationRequired"
                name="registrationRequired"
                checked={formData.registrationRequired}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="registrationRequired" className="ml-2 block text-sm text-gray-900">
                Registration Required
              </label>
            </div>

            
            {formData.registrationRequired && (
              <div className="ml-6 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How do people confirm they're coming?
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="attendanceMode_registration"
                        name="attendanceMode"
                        value="registration"
                        checked={formData.attendanceMode !== 'rsvp'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="attendanceMode_registration" className="ml-2 block text-sm text-gray-900">
                        Full registration form — for hackathons and workshops
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="attendanceMode_rsvp"
                        name="attendanceMode"
                        value="rsvp"
                        checked={formData.attendanceMode === 'rsvp'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="attendanceMode_rsvp" className="ml-2 block text-sm text-gray-900">
                        Quick RSVP (going / maybe / can't go) — for conclaves and meetups
                      </label>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    RSVP is one tap for signed-in members. The registration form collects full details and supports guests.
                  </p>
                </div>

                <div className={formData.attendanceMode === 'rsvp' ? 'opacity-50 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Type
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="registrationType_mandatory"
                        name="registrationType"
                        value="mandatory_signup"
                        checked={formData.registrationType === 'mandatory_signup'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="registrationType_mandatory" className="ml-2 block text-sm text-gray-900">
                        Registration Required (Mandatory Signup/Signin with UP ID)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="registrationType_optional"
                        name="registrationType"
                        value="optional_signup"
                        checked={formData.registrationType === 'optional_signup'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="registrationType_optional" className="ml-2 block text-sm text-gray-900">
                        Registration Required (No Mandatory Sign up with UP ID)
                      </label>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose whether users must have a Udyogapramoda account or can register as guests.
                  </p>
                </div>

                {/* Only meaningful for a hackathon; an ordinary event has no teams. */}
                {formData.eventType === 'hackathon' && (
                  <HackathonLinkField
                    value={formData.hackathon}
                    onChange={(id) => setFormData(prev => ({ ...prev, hackathon: id }))}
                    eventId={event._id}
                  />
                )}

                <div className="border-t pt-4">
                  <label htmlFor="communityInviteUrl" className="block text-sm font-medium text-gray-700 mb-1">
                    Group link for this event
                  </label>
                  <input
                    type="url"
                    id="communityInviteUrl"
                    name="communityInviteUrl"
                    value={formData.communityInviteUrl}
                    onChange={handleInputChange}
                    placeholder="https://chat.whatsapp.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Sent in the confirmation email and shown after registering. A WhatsApp or
                    Discord invite. Leave blank to use the community link from Site Settings.
                  </p>
                </div>

                {/* Questions are asked either way. Members answer them alongside
                    the details already on their profile; guests answer them as
                    part of the guest form. */}
                <div className="border-t pt-4">
                  <CustomFormFieldsManager
                    fields={formData.guestFormFields || []}
                    onChange={(fields) => setFormData(prev => ({ ...prev, guestFormFields: fields }))}
                  />
                </div>
              </div>
            )}<div className="flex items-center">
              <input
                type="checkbox"
                id="requiresAdminApproval"
                name="requiresAdminApproval"
                checked={formData.requiresAdminApproval}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="requiresAdminApproval" className="ml-2 block text-sm text-gray-900">
                Approve each registration myself
                <span className="block text-xs font-normal text-gray-500">
                  People who register wait as pending until you approve them on the
                  Registrations tab. Leave unticked to confirm everyone automatically.
                </span>
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPaidEvent"
                name="isPaidEvent"
                checked={formData.isPaidEvent}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isPaidEvent" className="ml-2 block text-sm text-gray-900">
                Paid Event
              </label>
            </div>

            {formData.isPaidEvent && (
              <div>
                <label htmlFor="eventFee" className="block text-sm font-medium text-gray-700 mb-1">
                  Event Fee (₹) *
                </label>
                <input
                  type="number"
                  id="eventFee"
                  name="eventFee"
                  value={formData.eventFee}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  required={formData.isPaidEvent}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter event fee amount"
                />
                <p className="text-sm text-gray-500 mt-1">Enter 0 for free events that require registration</p>
              </div>
            )}
          </div>

          {/* Speakers Section */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Event Speakers</h3>
              <Button type="button" onClick={addSpeaker} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Speaker
              </Button>
            </div>
            
            {formData.speakers.map((speaker, index) => (
              <div key={speaker.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Speaker {index + 1}</h4>
                  <Button 
                    type="button" 
                    onClick={() => removeSpeaker(speaker.id)} 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salutation
                    </label>
                    <input
                      type="text"
                      value={speaker.salutation}
                      onChange={(e) => updateSpeaker(speaker.id, 'salutation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Dr., Mr., Ms., etc."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={speaker.name}
                      onChange={(e) => updateSpeaker(speaker.id, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Speaker name"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bio
                    </label>
                    <textarea
                      value={speaker.bio}
                      onChange={(e) => updateSpeaker(speaker.id, 'bio', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Speaker biography"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Achievements
                    </label>
                    <textarea
                      value={speaker.achievements}
                      onChange={(e) => updateSpeaker(speaker.id, 'achievements', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Speaker achievements and credentials"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Picture
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleSpeakerPictureChange(speaker.id, e)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {speaker.picture && (
                      <div className="mt-2">
                        <p className="text-sm text-green-600 mb-2">✓ New: {speaker.picture.name}</p>
                        <img
                          src={URL.createObjectURL(speaker.picture)}
                          alt="Speaker preview"
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                        />
                      </div>
                    )}
                    {!speaker.picture && formData.existingSpeakerPictures[speaker.id] && (
                      <div className="mt-2">
                        <p className="text-sm text-blue-600 mb-2">✓ Current picture uploaded</p>
                        <img
                          src={formData.existingSpeakerPictures[speaker.id]}
                          alt="Current speaker"
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Topic
                    </label>
                    <input
                      type="text"
                      value={speaker.topic}
                      onChange={(e) => updateSpeaker(speaker.id, 'topic', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Speaking topic"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Event Flyer Section */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-lg font-medium text-gray-900">Event Flyer</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Flyer (PDF or Image - Max 10MB)
              </label>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={handleFlyerChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {/* Show existing flyer */}
              {formData.existingEventFlyer && !formData.eventFlyer && (
                <div className="mt-2">
                  <p className="text-sm text-blue-600 mb-2">✓ Current flyer uploaded</p>
                  {formData.existingEventFlyer.toLowerCase().includes('.pdf') ? (
                    <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center border-2 border-gray-200">
                      <span className="text-red-600 font-bold text-xs">PDF</span>
                    </div>
                  ) : (
                    <img
                      src={formData.existingEventFlyer}
                      alt="Current flyer"
                      className="w-16 h-16 rounded-lg object-cover border-2 border-gray-200"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = `
                          <div class="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center border-2 border-gray-200">
                            <span class="text-blue-600 font-bold text-xs">IMG</span>
                          </div>
                        `;
                      }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={handleRemoveFlyer}
                    className="text-red-600 hover:text-red-700 text-sm mt-2 underline"
                  >
                    Remove Flyer
                  </button>
                </div>
              )}
              
              {/* Show new flyer */}
              {formData.eventFlyer && (
                <div className="mt-2">
                  <p className="text-sm text-green-600 mb-2">✓ New: {formData.eventFlyer.name}</p>
                  {formData.eventFlyer.type.includes('pdf') ? (
                    <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center border-2 border-gray-200">
                      <span className="text-red-600 font-bold text-xs">PDF</span>
                    </div>
                  ) : (
                    <img
                      src={URL.createObjectURL(formData.eventFlyer)}
                      alt="Flyer preview"
                      className="w-16 h-16 rounded-lg object-cover border-2 border-gray-200"
                    />
                  )}
                  <button
                    type="button"
                    onClick={handleRemoveFlyer}
                    className="text-red-600 hover:text-red-700 text-sm mt-2 underline"
                  >
                    Remove Flyer
                  </button>
                </div>
              )}
            </div>
            
            {/* Show in Home Carousel Checkbox */}
            <div className="mt-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="showInHomeCarousel"
                  checked={formData.showInHomeCarousel}
                  onChange={handleInputChange}
                  disabled={!formData.eventFlyer && !formData.existingEventFlyer}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                />
                <span className={`text-sm font-medium ${!formData.eventFlyer && !formData.existingEventFlyer ? 'text-gray-400' : 'text-gray-700'}`}>
                  Show event flyer in home page carousel
                  {!formData.eventFlyer && !formData.existingEventFlyer && (
                    <span className="text-xs text-gray-400 block">
                      (Upload a flyer first to enable this option)
                    </span>
                  )}
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Check this to display the event flyer in the home page carousel (only if flyer is uploaded)
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

const ViewEventModal: React.FC<{ event: EventWithHackathon; onClose: () => void }> = ({ event, onClose }) => {

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto relative">
        {/* Close button at top right */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>

        <h2 className="text-2xl font-bold mb-4 pr-12">{event.title}</h2>
        
        {/* Basic Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Description</p>
              <p className="text-gray-900 mt-1">{event.description}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Category</p>
              <p className="text-gray-900 mt-1">{event.category || event.eventType}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Location</p>
              <p className="text-gray-900 mt-1">{event.location || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Virtual Event</p>
              <p className="text-gray-900 mt-1">{event.isVirtual ? 'Yes' : 'No'}</p>
            </div>
            {event.isVirtual && event.meetingLink && (
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-700">Meeting Link</p>
                <a href={event.meetingLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline mt-1 block">
                  {event.meetingLink}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Schedule & Registration */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">Schedule & Registration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Start Date & Time</p>
              <p className="text-gray-900 mt-1">{new Date(event.startTime).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">End Date & Time</p>
              <p className="text-gray-900 mt-1">{new Date(event.endTime).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Capacity</p>
              <p className="text-gray-900 mt-1">{event.capacity || 'Unlimited'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Current Registrations</p>
              <p className="text-gray-900 mt-1">{event.currentRegistrations || 0}</p>
            </div>
            {event.registrationDeadline && (
              <div>
                <p className="text-sm font-medium text-gray-700">Registration Deadline</p>
                <p className="text-gray-900 mt-1">{new Date(event.registrationDeadline).toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>

        {/* Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Registration Required</p>
              <p className="text-gray-900 mt-1">{event.registrationRequired ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Admin Approval Required</p>
              <p className="text-gray-900 mt-1">{event.requiresAdminApproval ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Paid Event</p>
              <p className="text-gray-900 mt-1">{event.isPaidEvent ? 'Yes' : 'No'}</p>
            </div>
            {event.isPaidEvent && (
              <div>
                <p className="text-sm font-medium text-gray-700">Event Fee</p>
                <p className="text-gray-900 mt-1">₹{event.eventFee || 0}</p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-700">Show Participant Count</p>
              <p className="text-gray-900 mt-1">{event.showParticipantCount !== false ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Status</p>
              <p className="text-gray-900 mt-1">{event.isActive ? 'Active' : 'Inactive'}</p>
            </div>
          </div>
        </div>

        {/* Event Speakers */}
        {event.speakers && event.speakers.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">Event Speakers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {event.speakers.map((speaker: any, index: number) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300 overflow-hidden">
                      {speaker.picture ? (
                        <img 
                          src={speaker.picture} 
                          alt={speaker.name}
                          className="w-full h-full object-cover"
                          onError={async (e) => {
                            const target = e.target as HTMLImageElement;
                            
                            // Try to get signed URL as fallback for private images
                            try {
                              const key = speaker.picture.split('/').pop();
                              if (key) {
                                const response = await fetch(`/api/upload/speaker-picture-url/${key}`);
                                if (response.ok) {
                                  const data = await response.json();
                                  if (data.success && data.url) {
                                    target.src = data.url;
                                    return;
                                  }
                                }
                              }
                            } catch (error) {
                              console.error('Failed to get signed URL for speaker image:', error);
                            }
                            
                            // If signed URL also fails, show initials
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<span class="text-gray-500 text-xs font-medium">${speaker.name.charAt(0).toUpperCase()}</span>`;
                            }
                          }}
                        />
                      ) : (
                        <span className="text-gray-500 text-xs font-medium">
                          {speaker.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {speaker.salutation && `${speaker.salutation} `}{speaker.name}
                      </h4>
                      {speaker.topic && (
                        <p className="text-sm text-blue-600 font-medium mt-1">{speaker.topic}</p>
                      )}
                      {speaker.bio && (
                        <p className="text-sm text-gray-600 mt-2">{speaker.bio}</p>
                      )}
                      {speaker.achievements && (
                        <div className="mt-2">
                          <span className="text-xs font-medium text-gray-700">Achievements:</span>
                          <p className="text-xs text-gray-600 mt-1">{speaker.achievements}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Event Flyer */}
        {event.eventFlyer && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">Event Flyer</h3>
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {event.eventFlyer.toLowerCase().includes('.pdf') ? (
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <span className="text-red-600 font-bold text-xs">PDF</span>
                    </div>
                  ) : (
                    <img 
                      src={event.eventFlyer} 
                      alt="Event Flyer"
                      className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = `
                          <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span class="text-blue-600 font-bold text-xs">IMG</span>
                          </div>
                        `;
                      }}
                    />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Event Flyer</p>
                  <a 
                    href={event.eventFlyer} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    View/Download Flyer
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default EventManagement; 